import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronLeft, Link2 as LinkIcon, Workflow, AlertTriangle, Bookmark, GitCompare, Flag, DollarSign, Zap, GitFork, UserCircle, Clock, Bell, Layers, Wand2 as Wand2Icon, ListChecks as ListChecksIcon, Clock as ClockIcon } from "lucide-react";

/**
 * Public Knowledge Base — accessible without login.
 * Covers Chain Reconciliation features: templates, branching/conflict,
 * Sankey reading, anomaly thresholds, cost attribution, dry-run, run diff,
 * chain→exception workflow.
 */
const ARTICLES = [
  {
    id: "midchain-forking",
    title: "Mid-chain forking (Phase B.2)",
    icon: GitFork,
    color: "#8B5CF6",
    body: [
      "**What it does.** Lets a chain fork DEEP in the walk, not just at hop 0. If at hop 2 your chain's seed row matches 3 payment records, the chain splits into 3 distinct chains, each carrying the same history up to hop 1 and diverging at hop 2.",
      "**Why it matters.** Real-world chains fork at every level. A single invoice can be paid in 2 installments (hop 1 → hop 2 fork); a single PO can have 3 invoices (hop 0 → hop 1 fork). Without mid-chain forking, you silently lose those chains.",
      "**How it works.** The engine runs normally. A post-pass scans the target file at EACH hop for additional candidates sharing the matched composite key. When extras are found, the primary chain extends through the first match; each additional match spawns a **fork** — a new `chain_id` with the parent's nodes[:N] deep-copied, plus its own hop-N node.",
      "**In the UI.** Forked chains show a small purple fork icon next to their chain_id in the waterfall. Click the row to open the detail panel and see `forked from <parent-id> at hop N`.",
      "**Branch cap applies per hop.** If `branch_cap=3`, each hop allows up to 3 matches per seed. Anything beyond tallies as `branches_dropped`.",
      "**When NOT to use it.** If your upstream data is genuinely 1:1 (each PO has exactly one invoice), leave `branch_cap=1`. Unnecessary forking creates noise.",
    ],
  },
  {
    id: "collaboration",
    title: "Chain ownership & comments",
    icon: UserCircle,
    color: "#14B8A6",
    body: [
      "**What it does.** Every chain in a run can be assigned an owner (by email) and can accumulate threaded comments. Great for month-end close where broken or conflicted chains need someone to investigate.",
      "**How to assign.** Click any chain row in the waterfall → the side panel opens. Type the assignee's email and click **Save**. Leave blank to unassign.",
      "**How to comment.** In the same side panel, type into the comment box and press Enter (or click Post). Comments are timestamped with the author's email, shown newest-last.",
      "**Where it lives.** Assignment + comments persist on the chain sub-document inside the chain_run. Re-running the chain creates a fresh run with an empty comment thread by design (comments stay tied to their historical run for audit purposes).",
      "**Integrations.** Use the `Flag` button on broken chains to also create a formal exception ticket — chain comments and exception comments are complementary: chain comments are lightweight review notes; exception tickets are full workflow items with assignment, transitions, and audit log.",
    ],
  },
  {
    id: "scheduling",
    title: "Scheduled reconciliation & email alerts",
    icon: Clock,
    color: "#8B5CF6",
    body: [
      "**Set it once, run automatically.** Navigate to **Schedules** in the sidebar and click *New schedule*. Pick a name, a cron expression (or a preset like 'Every Monday at 09:00'), and the kind: `chain_template` (recommended), `chain`, `multi`, or `single`.",
      "**Why use chain templates with schedules.** Month-end close touches the same files every month. Combine a Chain Template (sequence of hops + rule sets) with a schedule (cadence) and you have an unattended month-end close pipeline.",
      "**What happens on each run.** ReconX executes the payload like any other run, records the result in Run History, and — if the user has opted in — emails the summary + an open-the-run link. For chain runs with the anomaly flag, a separate amber-banner email is sent as well.",
      "**Failure handling.** If a scheduled run errors (bad template, missing files, rule set deleted), the schedule is marked 'Last run failed' and an email goes to the schedule creator automatically (this channel is always on — you can't accidentally silence failures).",
      "**Timezone note.** All cron expressions are interpreted in UTC. For '9am Paris', use '0 7 * * *' during CET or '0 6 * * *' during CEST.",
      "**Manual trigger.** Each schedule row has a ▶ button that runs it once immediately (useful for end-of-cycle or smoke-testing a newly saved schedule).",
      "**Pricing.** Schedules + email + PDF-to-inbox are part of the **Close Automation** tier (€249/mo). Lower tiers see the Schedules page but with an upsell banner — you can still create schedules, but the marketing reminds you this is the premium combo.",
    ],
  },
  {
    id: "notifications",
    title: "Notification preferences — what to get emailed",
    icon: Bell,
    color: "#EAB308",
    body: [
      "**Per-user, not per-department.** Each user picks what they want to hear about in **Notifications** (profile menu). Changes apply only to you.",
      "**Events supported (ON by default):**",
      "• `run_complete_single` — single-file reconciliation finished",
      "• `run_complete_multi` — multi-file reconciliation finished",
      "• `run_complete_chain` — chain reconciliation finished",
      "• `schedule_failed` — a scheduled run errored (highly recommended to keep ON)",
      "• `anomaly_triggered` — a chain run hit its branches_dropped threshold",
      "• `chain_assigned` — you were set as owner of a chain",
      "**SMTP prerequisite.** The superadmin must configure SMTP in Settings → Email. Without it, notifications are logged but not delivered. The **Send test email** button on the Notifications page verifies your setup end-to-end.",
      "**Unsubscribe is one click.** Toggle any event off and hit *Save* — that user stops receiving it immediately. No confirmation email, no hassle.",
    ],
  },
  {
    id: "chain-templates",
    title: "Chain Templates — set once, run every month",
    icon: Bookmark,
    color: "#0EA5E9",
    body: [
      "**What it is.** A chain template is a saved recipe for a multi-hop reconciliation walk (e.g. PO → Invoice → Payment → Bank). It captures: the anchor file reference, each hop's target file / rule set / transform set, anchor-source strategy (flat, cascade, or relay), branch cap, anomaly threshold, and cost column.",
      "**Why it matters.** Month-end close touches the same files and rules every period. Templates let an admin configure the walk once; analysts just click *Load template* in the Chain wizard next month, upload fresh files, and go.",
      "**Set up in 4 steps:**",
      "1. Open **Chain Reconciliation** from the sidebar.",
      "2. Configure the run — anchor file, each hop (file + rule set + anchor source), branch cap, cost column.",
      "3. Click **Save as template** (top-right of the Advanced / Templates card). Give it a name and description.",
      "4. Next month, pick the template from the **Templates** dropdown — all hop slots repopulate. Swap in the new month's files and hit **Run**.",
      "**Permissions.** Only `dept_admin` and `superadmin` can create, update, or delete templates. Any user in the department can *use* a template.",
      "**Where they live.** Templates live in MongoDB (`chain_templates` collection) scoped by department_id, and are listed at **/chain-templates**.",
    ],
  },
  {
    id: "branching",
    title: "Branching & conflict resolution",
    icon: Workflow,
    color: "#8B5CF6",
    body: [
      "**Why.** Real-world chains fork: one PO can have two invoices; one invoice can pay from two payment records. A strict 1:1 policy hides this — branching exposes it.",
      "**Branch cap.** Set in the wizard's *Advanced settings*. Default `1` = pure 1:1 (first match wins, rest dropped as `branches_dropped`). Set to `3` to keep up to 3 branches per anchor; anything beyond is still tallied as dropped.",
      "**What you'll see.** At hop 0, when an anchor matches N target rows, the engine spawns up to `branch_cap` chains (one per kept match). Each chain gets its own `chain_id` so you can follow each branch downstream independently.",
      "**Conflicts.** When two separate chains both match the SAME target row at the SAME hop (e.g. two POs claim the same invoice), that's a conflict. The results page shows an orange **Conflicts panel** with a *Resolve →* button: pick which chain wins; the others are flagged `conflict_rejected_at_hop_{N}`.",
      "**Tips.**",
      "- If `branches_dropped` is consistently high for one rule, that rule may be *over-matching* (e.g. join key is too loose). Use the **Anomaly banner** to catch this automatically.",
      "- Resolved conflicts live on the run permanently — rerunning the chain rebuilds the conflict set from scratch.",
    ],
  },
  {
    id: "sankey",
    title: "Reading the Sankey flow diagram",
    icon: Workflow,
    color: "#6366F1",
    body: [
      "**What it shows.** Every chain traced horizontally across the hops. Thicker bands = more chains flowing through. Three terminal nodes on the right: green **Complete**, red **Broken**, yellow **Ignored**.",
      "**How to read it.**",
      "- **Top flow:** Look at which hop drops the most chains. If Hop 2 → Broken is a thick red band, your rule set at hop 2 is your weakest link.",
      "- **Thickness of the Complete band:** your overall end-to-end reconciliation rate.",
      "- **Ignored band:** chains excluded by explicit ignore rules. Thick yellow bands usually indicate rule-set design intent (e.g. exclude inter-company entries).",
      "**Color legend.** Purple = in-flight hop, Green = complete, Red = broken, Yellow = ignored.",
    ],
  },
  {
    id: "anomaly",
    title: "Anomaly flagging — knowing when to worry",
    icon: AlertTriangle,
    color: "#F59E0B",
    body: [
      "**What it catches.** When `branches_dropped` as a fraction of total anchor rows crosses your threshold, the results page shows an amber banner: *'branches_dropped (N) is X% of anchor — above Y% threshold.'*",
      "**Why it matters.** A high drop rate usually means a rule is *over-matching*: your join key is too loose (e.g. matching on a customer name that repeats across many invoices). Left unchecked, you silently lose data. Anomaly flagging makes it visible.",
      "**Tuning the threshold.** Default is `0.2` (20%). For ledgers with lots of duplicates, loosen to `0.4`; for strict 1:1 pipelines, tighten to `0.1`.",
      "**Action when flagged.** Open the *Branched* tab on the waterfall, inspect a few example chains, and decide: (a) tighten the rule's match keys, (b) raise `branch_cap` and accept the branching, or (c) ignore the noise.",
    ],
  },
  {
    id: "cost",
    title: "Cost attribution across the chain",
    icon: DollarSign,
    color: "#0891B2",
    body: [
      "**The problem.** Finance cares about *dollars* walked through the chain, not just row counts. If a PO→Invoice→Payment chain broke at hop 2, how much money is stuck?",
      "**How it works.** In the wizard's Advanced settings, set **Cost column (anchor)** — it can be left blank (we auto-detect the first numeric column that's ≥60% numeric). Per hop, you can also set an individual *cost column* on the target file.",
      "**What you'll see.** On the results page:",
      "- Summary card **Cost reconciled**: total $ across all COMPLETE chains, with an *at risk* subtitle showing $ across BROKEN chains.",
      "- Each chain node stores its `cost` (source-side) and `matched_cost` (target-side). Sum both for the chain's `cost_total`.",
      "- Export CSV includes `cost_total` as a column.",
      "**Gotcha.** The column name must exist in the raw file *after* any transform-set rewriting. If your transforms rename `gross_amt` to `amount`, pick `amount`.",
    ],
  },
  {
    id: "dry-run",
    title: "What-if / Dry-run mode",
    icon: Zap,
    color: "#EAB308",
    body: [
      "**Use case.** You're about to try a new rule set, or swap in a new cascade strategy, but don't want to pollute your run history.",
      "**How.** Check the **Dry run** box in the Advanced settings before clicking *Run*. The engine executes every hop, computes the full summary, then returns — no `chain_run` document is persisted, no `recon_results` are stored.",
      "**What you get.** A modal pops up showing: anchor rows, chains complete, broken, ignored, branches dropped, and the anomaly verdict.",
      "**What you don't get.** A results page to drill into. Dry runs are summary-only by design.",
      "**Pro tip.** Use dry-run while iterating on a new chain template — once the summary looks right, uncheck the box and run for real.",
    ],
  },
  {
    id: "diff",
    title: "Comparing two runs (month-over-month)",
    icon: GitCompare,
    color: "#059669",
    body: [
      "**Use case.** 'What chains improved this month? What chains broke that were clean last month?' Classic month-over-month audit question.",
      "**How.** On the Results page, click **Diff**, pick a previous run from the dropdown, click **Compare**.",
      "**What it shows.**",
      "- **Newly complete**: anchor rows that are `complete` in THIS run but were not `complete` in the other run.",
      "- **Newly broken**: anchor rows that WERE `complete` in the other run but are not in this run.",
      "- **Unchanged**: anchor rows whose status is identical across both runs.",
      "**Key identity.** Diff matches anchors by a structural hash of their non-internal columns. As long as the raw anchor rows haven't been restructured, the comparison is deterministic.",
    ],
  },
  {
    id: "hub-and-spoke",
    title: "Hub-and-Spoke reconciliation",
    icon: Workflow,
    color: "#2563EB",
    body: [
      "**What it is.** One anchor (hub) file is reconciled INDEPENDENTLY against N target (spoke) files. Every spoke uses the ANCHOR as its source of truth — there's no hand-off between spokes. Think: a GL trial balance reconciled against (a) the bank feed, (b) the AP sub-ledger, and (c) the AR sub-ledger — all three are separate conversations with the same hub.",
      "**When to use it.** You have one 'master' ledger and multiple downstream systems to prove against it. The spokes don't depend on each other; each answer stands alone.",
      "**Contrast with Cascade.** Cascade chains spokes TOGETHER: spoke 2 takes the matched output of spoke 1 as its anchor. Hub-and-Spoke does not — each spoke starts fresh from the hub.",
      "**How to run it in ReconX.** Open **Multi-file Reconciliation**, upload your anchor + each target, and for every target set `anchor_source = flat` (the default). Each target gets its own rule set. Results show one card per target with its own matched/partial/ignored counts.",
      "**When NOT to use it.** If your question is 'did this PO make it all the way to a bank outflow?' — use **Chain Reconciliation** instead. Hub-and-Spoke can't trace a single row through multiple systems.",
    ],
  },
  {
    id: "multi-cascade",
    title: "Multi-file Cascade Anchoring",
    icon: Layers,
    color: "#7C3AED",
    body: [
      "**What it is.** A multi-file walk where each target's anchor changes depending on the previous target's outcome. Target 2's anchor = Target 1's MATCHED set. Target 3's anchor = Target 2's MATCHED set. And so on.",
      "**Why this is powerful.** You can answer: 'Of the POs that made it to invoices, how many then made it to payment, and of those, how many actually cleared the bank?' — as a single pipeline.",
      "**Set the anchor source per hop.** In **Multi-file Reconciliation** each target has an `anchor_source` dropdown:",
      "• `flat` → always use the original anchor file (Hub-and-Spoke behaviour)",
      "• `prev_matched` → use ONLY rows that matched in the previous hop (Cascade — narrowing)",
      "• `prev_unmatched` → use the rows the previous hop COULD NOT match (Cascade — branching into fallback sources)",
      "**Tip.** Mix and match: hop 1 can use `flat`, hop 2 can cascade with `prev_matched`, hop 3 can go back to `flat` — the engine supports arbitrary per-hop combinations.",
      "**Difference from Chain.** Cascade is ROW-LEVEL narrowing (still a bag of rows). Chain Reconciliation is an explicit PER-ANCHOR walk — each anchor row becomes a chain_id you can follow end-to-end. Use Cascade for bulk funnel metrics; use Chain when you need to trace individual records.",
    ],
  },
  {
    id: "exact-vs-fuzzy",
    title: "Exact vs Fuzzy matching",
    icon: GitCompare,
    color: "#DB2777",
    body: [
      "**Exact match.** Deterministic. `A.po_number == B.po_number`. Fast, no false positives. Breaks when data is messy (whitespace, casing, leading zeros stripped by Excel).",
      "**Fuzzy match.** Uses RapidFuzz (local, rule-based — **no LLM**) to score string similarity. 100 = identical; typical thresholds 85–95 for tight finance data. Trades speed and some false positives for robustness against typos and format drift.",
      "**Best practice — normalize FIRST, fuzzy LAST.** Use **Cleanup Tables** to strip whitespace, fix casing, and pad leading zeros. Use **Transform Sets** to coerce dates and amounts. After normalization, exact match handles 95% of real-world cases — reserve fuzzy for the stubborn remainder (free-text descriptions, names).",
      "**Threshold guidance.**",
      "• ≥95 for invoice numbers and IDs that SHOULD be exact after normalization",
      "• 88–92 for party names (customer/vendor)",
      "• 80–85 for free-text descriptions",
      "**Auditor note.** Fuzzy matches get a `confidence_score` in the result row. Lower scores warrant a human review; they're not hidden behind an auto-approve.",
    ],
  },
  {
    id: "transform-lookup-cleanup",
    title: "Transforms, Lookups & Cleanups — the data-prep trio",
    icon: Wand2Icon,
    color: "#059669",
    body: [
      "**Transform Sets.** Ordered operations applied to a file BEFORE matching. Examples: parse `date_str` → ISO date, strip currency symbols from `amount`, concatenate `first_name + last_name` → `full_name`. Defined once, reused across runs.",
      "**Lookup Tables.** Static CSV-like reference data stored in ReconX (e.g. country-code → country-name, vendor-id → vendor-name). Used inside a transform step: 'replace `vendor_id` with `vendor_name` from lookup <Vendor Master>'. Great for enriching sparse source files before matching.",
      "**Cleanup Tables.** Simple find-and-replace lists (e.g. 'Inc.' → '', 'LLC' → ''). Runs before fuzzy matching to normalize entity names. Can be applied to any column.",
      "**When to use which.** Transform = compute/reshape. Lookup = enrich from reference. Cleanup = normalize/strip noise. Chain them in a Transform Set for end-to-end prep: cleanup → lookup → compute.",
      "**Why this matters for reconciliation quality.** 80% of match failures are data-format mismatches, not rule logic. Invest in cleanups and transforms FIRST; only then tune match thresholds.",
    ],
  },
  {
    id: "composite-key",
    title: "Designing composite match keys",
    icon: ListChecksIcon,
    color: "#0891B2",
    body: [
      "**Why composite.** Real records rarely have a single perfect join column. Invoice # alone is ambiguous across vendors; vendor_id alone is ambiguous across invoices. Combine them — (vendor_id, invoice_number) — for a unique hit.",
      "**How to declare it in ReconX.** In **Rule Builder**, add a single rule with MULTIPLE `match_conditions`. Each condition is a column pair + comparator. The rule matches only when ALL conditions hold (AND logic).",
      "**Example rule — strict invoice match:**",
      "• Condition 1: `A.vendor_id` **equals** `B.vendor_id`",
      "• Condition 2: `A.invoice_no` **equals** `B.invoice_no`",
      "• Condition 3: `A.invoice_date` **within 2 days of** `B.posting_date`",
      "**Layering rules — first match wins.** Declare tight rules FIRST (sequence 1) and looser fallback rules LATER (sequence 2, 3, …). A row gets matched by the first rule whose conditions hold; if no rule fires, it goes to `unmatched`.",
      "**Tip.** Keep rules named in business language — `invoice_exact`, `invoice_fuzzy_vendor`, `amount_only_fallback`. The name flows through to the results (and chain waterfall nodes), making audits readable.",
    ],
  },
  {
    id: "exceptions-workflow",
    title: "Exceptions — turning unmatched into action",
    icon: Flag,
    color: "#F97316",
    body: [
      "**Reconciliation without follow-up is reporting, not controls.** An exception is a formal ticket for an unmatched or broken row. It has: owner, status (open → in-progress → resolved), audit log, and a comment thread.",
      "**How to create one.** On any single/multi/chain result row, click the small orange 🚩 icon. The exception is pre-filled with a snapshot of the row and a link back to the source run.",
      "**Bulk triage.** From the Exceptions dashboard, filter by age, flag (amount_mismatch, missing_in_target, …), owner, or department. Assign in bulk, comment, resolve, re-open.",
      "**Status transitions.** `open` → `in-progress` → `resolved` or `rejected`. Every transition is timestamped with the actor's email. Exceptions can be reopened; the history is append-only.",
      "**Why chain → exception is a big deal.** A broken chain tells you WHERE a process collapsed; an exception tells you WHO owns fixing it. Use the flag on the Chain results page to bridge the two.",
    ],
  },
  {
    id: "run-history-audit",
    title: "Run history & audit trail",
    icon: ClockIcon,
    color: "#6D28D9",
    body: [
      "**Every run is permanent.** Single, multi, and chain runs are stored immutably in MongoDB. Exports, diffs, and rerun buttons all reference the run by ID. Runs can be hidden from the dashboard but never silently overwritten.",
      "**Who did what.** Every run records `created_by` (email) and `created_at` (UTC ISO). Scheduled runs additionally carry `schedule_id` so you can trace back to the cron configuration.",
      "**Audit exports.** From any result page, **Export CSV** and **Open HTML report** give you stable artefacts for an auditor. The HTML report is self-contained (no login required to view) — hand it directly to external reviewers.",
      "**Month-over-month diff.** Results pages expose a **Diff** action: pick a previous run, get `newly_complete`, `newly_broken`, and `unchanged`. Backed by row-structural hashing, so it survives incidental column reordering.",
      "**Retention tip.** Keep at least 12 months of runs for SOX-style reviews. Older runs can be archived outside ReconX; the metadata (who/when/result) remains searchable in Dept Admin → Runs.",
    ],
  },
];

export default function KnowledgeBasePage() {
  const [active, setActive] = useState("chain-templates");
  const art = ARTICLES.find(a => a.id === active) || ARTICLES[0];
  const Icon = art.icon;
  const formatLine = (line) => {
    // Very light markdown — bold **text**
    const parts = String(line).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**")) {
        return <b key={i}>{p.slice(2, -2)}</b>;
      }
      return <span key={i}>{p}</span>;
    });
  };
  return (
    <div data-testid="knowledge-base" style={{ minHeight: "100vh", background: "var(--bg-default)", color: "var(--text-primary)", fontFamily: "DM Sans, sans-serif" }}>
      <header style={{ padding: "1rem 1.5rem", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ textDecoration: "none", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 700 }}>
          <ChevronLeft size={18} /> Back to site
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <BookOpen size={18} /> <span style={{ fontWeight: 700 }}>Knowledge base</span>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: "2rem", maxWidth: 1180, margin: "2rem auto", padding: "0 1.5rem" }}>
        <aside>
          <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.2rem", margin: "0 0 0.75rem" }}>Reconciliation concepts</h2>
          <nav style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {ARTICLES.map(a => {
              const A = a.icon;
              return (
                <button key={a.id} data-testid={`kb-nav-${a.id}`}
                  onClick={() => setActive(a.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    padding: "0.5rem 0.75rem", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${active === a.id ? a.color : "transparent"}`,
                    background: active === a.id ? `${a.color}14` : "transparent",
                    color: active === a.id ? a.color : "var(--text-secondary)",
                    textAlign: "left", fontSize: "0.88rem", fontWeight: active === a.id ? 600 : 500,
                  }}>
                  <A size={14} /> {a.title}
                </button>
              );
            })}
          </nav>
        </aside>

        <article data-testid={`kb-article-${art.id}`} style={{ background: "white", border: "1px solid var(--border-default)", borderRadius: 12, padding: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
            <Icon size={22} style={{ color: art.color }} />
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.6rem", margin: 0 }}>{art.title}</h1>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", fontSize: "0.92rem", lineHeight: 1.6, color: "var(--text-primary)" }}>
            {art.body.map((line, i) => (
              <p key={i} style={{ margin: 0 }}>{formatLine(line)}</p>
            ))}
          </div>

          <div style={{ marginTop: "2rem", paddingTop: "1rem", borderTop: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: "0.75rem", fontSize: "0.82rem", color: "var(--text-muted)" }}>
            <LinkIcon size={14} />
            <span>Ready to try it? Head to the <Link to="/login">app</Link> and open <b>Chain Reconciliation</b>.</span>
          </div>
        </article>
      </div>
    </div>
  );
}
